/* UI */
import { Box } from '@auviknetworks/iceberg-ui';
import { Divider, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/*  Shared  */
import { FontSizes } from 'application/Shared/Constants';
import { Helpers, hmsFormatter } from 'application/Shared/Helpers';

/*  React  */
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';

/*  Constants  */
const { isset } = Helpers;

interface UsageOverviewType {
    name: string;
    [key: string]: string | number | undefined;
}

export const UsageOverview = ({
    renderingScheduledReport,
    reportData,
    reportWrapper,
    reportWrapperProps
}: {
    renderingScheduledReport: boolean;
    reportData: UsageOverviewType[];
    reportWrapper: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    reportWrapperProps: any;
}) => {
    const { t } = useTranslation();
    const { palette } = useTheme();

    // Get the name of the top 5 apps with the most usage (+ "Other Applications",
    // which is the accumulated value of the rest of the apps).
    // The "name" entry is the date in the format MM/DD/YY, that is why we exclude it
    const usageReportKeys = Array.from(
        reportData.reduce((acc, report) => {
            Object.keys(report).forEach((key) => {
                if (key !== 'name') acc.add(key);
            });
            return acc;
        }, new Set<string>())
    );

    if (renderingScheduledReport) {
        return (
            <Box>
                {!reportWrapper && (
                    <Typography variant="h5">
                        {t('Reports.UsageReport.usage_overview')}
                    </Typography>
                )}
                <ResponsiveContainer height={300} width="100%">
                    <BarChart
                        width={700}
                        height={240}
                        data={reportData}
                        margin={{
                            bottom: 5,
                            left: 20,
                            right: 30,
                            top: 20
                        }}
                    >
                        <CartesianGrid
                            stroke={palette.chart.axis}
                            strokeDasharray="3 3"
                        />
                        <XAxis dataKey="name" />
                        <YAxis tickFormatter={hmsFormatter} />
                        <Tooltip />
                        <Legend />
                        {usageReportKeys.map((row: string, index: number) => (
                            <Bar
                                isAnimationActive={false}
                                key={`iuo_${row}`}
                                dataKey={row}
                                stackId="a"
                                fill={
                                    palette.graph.colors[
                                        index % palette.graph.colors.length
                                    ]
                                }
                            />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            </Box>
        );
    }

    return (
        <Box margin={!isset(reportWrapperProps, 'height') ? '24px' : '0px'}>
            {!reportWrapper && (
                <>
                    <Typography variant="h5">
                        {t('Reports.UsageReport.usage_overview')}
                    </Typography>
                    <Divider
                        sx={{
                            borderBottomWidth: '4px',
                            borderColor: 'border.darkerDivider',
                            marginBottom: '16px',
                            marginTop: '8px'
                        }}
                    />
                </>
            )}
            <ResponsiveContainer height={300}>
                <BarChart
                    data={reportData}
                    margin={{
                        bottom: isset(reportWrapperProps, 'noLegend') ? -5 : 5,
                        left: 35,
                        right: 20,
                        top: 10
                    }}
                >
                    <CartesianGrid
                        stroke={palette.chart.axis}
                        strokeDasharray="3 3"
                    />
                    <XAxis dataKey="name" />
                    <YAxis tickFormatter={hmsFormatter} />
                    <Tooltip formatter={hmsFormatter} />
                    {!isset(reportWrapperProps, 'noLegend') && (
                        <Legend
                            iconSize={10}
                            layout="horizontal"
                            align="center"
                            wrapperStyle={{
                                fontSize: FontSizes[0],
                                top: '275px'
                            }}
                        />
                    )}
                    {usageReportKeys.map((row: string, index: number) => (
                        <Bar
                            key={`iuo_${row}`}
                            dataKey={row}
                            stackId="a"
                            fill={
                                palette.graph.colors[
                                    index % palette.graph.colors.length
                                ]
                            }
                        />
                    ))}
                </BarChart>
            </ResponsiveContainer>
        </Box>
    );
};
